import { useState, useEffect } from "react";

import {
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  FolderIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

import apiService from "../services/api";

const SUPPORTED_VARIABLES = [
  { key: "FIRSTNAME", description: "Customer's first name" },
  { key: "LASTNAME", description: "Customer's last name" },
  { key: "EMAIL", description: "Customer's email address" },
  { key: "TICKET_NUMBER", description: "Ticket number/ID" },
  { key: "TICKET_SUBJECT", description: "Ticket subject line" },
];

const PredefinedRepliesManager = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [replies, setReplies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingReply, setEditingReply] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
  });
  const [replyForm, setReplyForm] = useState({
    title: "",
    content: "",
    category_id: null,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, repliesRes] = await Promise.all([
        apiService.getPredefinedReplyCategories(),
        apiService.getPredefinedReplies(),
      ]);

      if (categoriesRes.status === 200) {
        setCategories(categoriesRes.data || []);
      }
      if (repliesRes.status === 200) {
        setReplies(repliesRes.data || []);
      }
    } catch (error) {
      toast.error("Failed to load predefined replies");
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");

      return;
    }

    try {
      setSaving(true);
      if (editingCategory) {
        await apiService.updatePredefinedReplyCategory(
          editingCategory.category_id,
          categoryForm
        );
        toast.success("Category updated successfully");
      } else {
        await apiService.createPredefinedReplyCategory(categoryForm);
        toast.success("Category created successfully");
      }
      setShowCategoryModal(false);
      setCategoryForm({ name: "", description: "" });
      setEditingCategory(null);
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const categoryReplies = replies.filter(
      (r) => r.category_id === category.category_id
    );

    if (categoryReplies.length > 0) {
      toast.error(
        `Cannot delete category with ${categoryReplies.length} replies. Move or delete replies first.`
      );

      return;
    }

    if (
      !window.confirm(`Are you sure you want to delete "${category.name}"?`)
    ) {
      return;
    }

    try {
      await apiService.deletePredefinedReplyCategory(category.category_id);
      toast.success("Category deleted successfully");
      if (selectedCategory?.category_id === category.category_id) {
        setSelectedCategory(null);
      }
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete category");
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
    setShowCategoryModal(true);
  };

  // Reply CRUD
  const handleSaveReply = async () => {
    if (!replyForm.title.trim()) {
      toast.error("Reply title is required");

      return;
    }
    if (!replyForm.content.trim() || replyForm.content.trim().length < 10) {
      toast.error("Reply content must be at least 10 characters");

      return;
    }

    try {
      setSaving(true);
      const formData = {
        ...replyForm,
        category_id: replyForm.category_id || selectedCategory?.category_id,
      };

      if (editingReply) {
        await apiService.updatePredefinedReply(editingReply.reply_id, formData);
        toast.success("Reply updated successfully");
      } else {
        await apiService.createPredefinedReply(formData);
        toast.success("Reply created successfully");
      }
      setShowReplyModal(false);
      setReplyForm({ title: "", content: "", category_id: null });
      setEditingReply(null);
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to save reply");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReply = async (reply) => {
    if (!window.confirm(`Are you sure you want to delete "${reply.title}"?`)) {
      return;
    }

    try {
      await apiService.deletePredefinedReply(reply.reply_id);
      toast.success("Reply deleted successfully");
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete reply");
    }
  };

  const openEditReply = (reply) => {
    setEditingReply(reply);
    setReplyForm({
      title: reply.title,
      content: reply.content,
      category_id: reply.category_id,
    });
    setShowReplyModal(true);
  };

  const openAddReply = () => {
    setEditingReply(null);
    setReplyForm({
      title: "",
      content: "",
      category_id: selectedCategory?.category_id || null,
    });
    setShowReplyModal(true);
  };

  // Insert variable into content
  const insertVariable = (variable) => {
    setReplyForm((prev) => ({
      ...prev,
      content: `${prev.content}[${variable}]`,
    }));
  };

  // Filter replies
  const filteredReplies = replies.filter((reply) => {
    const matchesCategory =
      !selectedCategory || reply.category_id === selectedCategory.category_id;
    const matchesSearch =
      !searchQuery ||
      reply.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reply.content.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Get uncategorized replies
  const uncategorizedReplies = replies.filter((r) => !r.category_id);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading predefined replies...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Predefined Replies
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage quick reply templates for support tickets
          </p>
        </div>

        <div className="flex gap-3">
          <button
            className="btn btn-secondary flex items-center gap-2"
            onClick={() => {
              setEditingCategory(null);
              setCategoryForm({ name: "", description: "" });
              setShowCategoryModal(true);
            }}
          >
            <FolderIcon className="w-5 h-5" />
            Add Category
          </button>
          <button
            className="btn btn-primary flex items-center gap-2"
            onClick={openAddReply}
          >
            <PlusIcon className="w-5 h-5" />
            Add Reply
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Categories Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Categories
            </h3>

            <div className="space-y-2">
              {/* All Replies */}
              <button
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                  !selectedCategory
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                onClick={() => setSelectedCategory(null)}
              >
                <span className="flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4" />
                  All Replies
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {replies.length}
                </span>
              </button>

              {/* Categories */}
              {categories.map((category) => {
                const categoryReplies = replies.filter(
                  (r) => r.category_id === category.category_id
                );

                return (
                  <div
                    className={`group rounded-lg transition-colors ${
                      selectedCategory?.category_id === category.category_id
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                    key={category.category_id}
                  >
                    <div className="flex items-center justify-between px-3 py-2">
                      <button
                        className={`flex-1 text-left flex items-center gap-2 ${
                          selectedCategory?.category_id === category.category_id
                            ? "text-blue-700 dark:text-blue-300"
                            : ""
                        }`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        <FolderIcon className="w-4 h-4" />
                        <span className="truncate">{category.name}</span>
                      </button>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {categoryReplies.length}
                        </span>
                        <button
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditCategory(category);
                          }}
                        >
                          <PencilIcon className="w-3 h-3" />
                        </button>
                        <button
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category);
                          }}
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Uncategorized */}
              {uncategorizedReplies.length > 0 && (
                <button
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between text-gray-500 ${
                    selectedCategory === "uncategorized"
                      ? "bg-gray-100 dark:bg-gray-700"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => setSelectedCategory("uncategorized")}
                >
                  <span className="flex items-center gap-2">
                    <DocumentTextIcon className="w-4 h-4" />
                    Uncategorized
                  </span>
                  <span className="text-sm">{uncategorizedReplies.length}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Replies List */}
        <div className="col-span-12 lg:col-span-9">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            {/* Search */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="Search replies..."
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Replies */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredReplies.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? "No replies match your search"
                    : "No replies in this category"}
                </div>
              ) : (
                filteredReplies.map((reply) => (
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    initial={{ opacity: 0 }}
                    key={reply.reply_id}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate">
                            {reply.title}
                          </h4>
                          {reply.category_name && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                              {reply.category_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {reply.content}
                        </p>
                        {reply.variables && reply.variables.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {reply.variables.map((v) => (
                              <span
                                className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded"
                                key={v}
                              >
                                [{v}]
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Used {reply.usage_count || 0}x
                        </span>
                        <button
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                          onClick={() => openEditReply(reply)}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg transition-colors"
                          onClick={() => handleDeleteReply(reply)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md"
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {editingCategory ? "Edit Category" : "Add Category"}
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={() => setShowCategoryModal(false)}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category Name *
                  </label>
                  <input
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Billing Issues"
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="Optional description..."
                    rows={3}
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCategoryModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex items-center gap-2"
                  disabled={saving}
                  onClick={handleSaveCategory}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      {editingCategory ? "Update" : "Create"}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reply Modal */}
      <AnimatePresence>
        {showReplyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {editingReply ? "Edit Reply" : "Add Reply"}
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={() => setShowReplyModal(false)}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title *
                  </label>
                  <input
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Welcome Message"
                    type="text"
                    value={replyForm.title}
                    onChange={(e) =>
                      setReplyForm({ ...replyForm, title: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    value={replyForm.category_id || ""}
                    onChange={(e) =>
                      setReplyForm({
                        ...replyForm,
                        category_id: e.target.value || null,
                      })
                    }
                  >
                    <option value="">Uncategorized</option>
                    {categories.map((cat) => (
                      <option key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Content *
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="Enter your reply template here..."
                    rows={8}
                    value={replyForm.content}
                    onChange={(e) =>
                      setReplyForm({ ...replyForm, content: e.target.value })
                    }
                  />
                </div>

                {/* Variable Placeholders */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Insert Variable Placeholders
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Click to insert. Variables will be replaced with actual
                    values when the reply is used.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUPPORTED_VARIABLES.map((v) => (
                      <button
                        className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        key={v.key}
                        title={v.description}
                        type="button"
                        onClick={() => insertVariable(v.key)}
                      >
                        [{v.key}]
                        <ChevronRightIcon className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowReplyModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex items-center gap-2"
                  disabled={saving}
                  onClick={handleSaveReply}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      {editingReply ? "Update" : "Create"}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PredefinedRepliesManager;
